/**
 * Trial units dashboard — staff view of radios currently in, or having
 * completed, a billing trial period. Fronts TrialRadioTable with status stat
 * cards (Active / Expiring / Scheduled / Ended), each toggling the grid to
 * that status via the shared date-range filter mapping; card counts come
 * from count-only requests (limit 1, meta.total) per status bucket,
 * decoupled from the grid's own paged/filtered fetch.
 *
 * Contains:
 * - TrialUnitsView — routed page at /billing/trial-units (default export)
 */

import { Box } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { listRadios } from '@sophocles/api/radios';
import StatCard, { StatCardGroup } from '@sophocles/components/StatCard';
import { toJsonApiParams } from '@sophocles/data-grids';
import { BillingTitleBar } from '@sophocles/views/billing/components';
import TrialRadioTable from '@sophocles/views/billing/components/TrialRadioTable';
import {
  TRIAL_SCOPE_PARAMS,
  TRIAL_STATUS_CARD_ORDER,
  TRIAL_STATUS_CHIPS,
  trialStatusDateFilter,
} from '@sophocles/views/billing/components/trialUnitStatus';

function TrialUnitsView() {
  const { t } = useTranslation();

  const [statusFilter, setStatusFilter] = useState(null);
  const [statusCounts, setStatusCounts] = useState({});
  const [trialTotal, setTrialTotal] = useState(null);
  const [countsLoading, setCountsLoading] = useState(false);

  // Decoupled from the grid's own fetch (which is paged/filtered) and from
  // the active status filter (cards must keep showing every category's
  // total, not just the one currently selected). Counts come from one
  // count-only request per status (limit 1, meta.total) — the server's
  // date-range filters resolve each bucket, so no row-level date fields are
  // required on the wire.
  useEffect(() => {
    let cancelled = false;
    setCountsLoading(true);
    const countRequest = extraParams =>
      listRadios(toJsonApiParams({ ...TRIAL_SCOPE_PARAMS, ...extraParams, limit: 1, offset: 0 })).then(
        response => response?.meta?.total ?? 0,
      );
    const today = new Date();
    Promise.all([
      countRequest({}),
      ...TRIAL_STATUS_CARD_ORDER.map(status => countRequest(trialStatusDateFilter(status, today))),
    ])
      .then(([total, ...statusTotals]) => {
        if (cancelled) return;
        setTrialTotal(total);
        setStatusCounts(Object.fromEntries(TRIAL_STATUS_CARD_ORDER.map((status, i) => [status, statusTotals[i]])));
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Failed to load trial unit status counts', error);
        setStatusCounts({});
        setTrialTotal(null);
      })
      .finally(() => {
        if (!cancelled) setCountsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isCardActive = useCallback(status => statusFilter === status, [statusFilter]);
  const handleCardClick = useCallback(status => {
    setStatusFilter(prev => (prev === status ? null : status));
  }, []);

  return (
    <>
      <BillingTitleBar showBackButton title={t('billing.trialUnits', 'Trial Units')} />

      <Box sx={{ flexShrink: 0, px: 'var(--page-px)', pt: 'var(--page-py)' }}>
        <StatCardGroup
          columns={{
            xs: 2,
            sm: Math.min(TRIAL_STATUS_CARD_ORDER.length + 1, 3),
            md: TRIAL_STATUS_CARD_ORDER.length + 1,
          }}
          sx={{ marginBottom: 'var(--page-gap)' }}
        >
          {/* All-up trial count; click clears the status filter. */}
          <StatCard
            title={t('billing.trialUnitsInTrial', 'In Trial')}
            value={countsLoading || trialTotal == null ? '—' : trialTotal}
            color="var(--md-primary)"
            loading={countsLoading}
            active={statusFilter == null}
            onClick={() => setStatusFilter(null)}
          />
          {TRIAL_STATUS_CARD_ORDER.map(status => {
            const chip = TRIAL_STATUS_CHIPS[status];
            return (
              <StatCard
                key={status}
                title={t(chip.labelKey, chip.fallback)}
                value={countsLoading ? '—' : (statusCounts[status] ?? 0)}
                color={chip.cssVar}
                loading={countsLoading}
                active={isCardActive(status)}
                onClick={() => handleCardClick(status)}
              />
            );
          })}
        </StatCardGroup>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          px: 'var(--page-px)',
          pb: 'var(--page-py)',
        }}
      >
        <TrialRadioTable statusFilter={statusFilter} />
      </Box>
    </>
  );
}

export default TrialUnitsView;
